'use client';

import {
  ConfirmDialog,
  Dialog,
  Flex,
  Text,
  Button,
  ButtonGroup,
  Input,
  Textarea,
  Alert,
  Loading,
  Column,
} from '@/_components';
import { FC, useState, useCallback, useContext, useEffect } from 'react';
import type { SecureDocumentShareDialogProps } from './SecureDocumentShareDialog.types.ts';
import {
  createSecureDocumentShare,
  checkExistingSecureShare,
  sendSecureShareEmail,
  expireSecureShare,
  type SecureDocumentShareResponse,
} from '@/_api/secure-document-shares';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { DocumentsContext } from '@/_contexts/DocumentsProvider';
import { useCopy } from '@/_hooks/useCopy';
import { downloadDocument } from '@/_api/client-documents';
import { getDocumentName } from '@/_utils/files';

export const SecureDocumentShareDialog: FC<SecureDocumentShareDialogProps> = ({
  isOpen,
  onClose,
  document,
  companyId,
  onSecureShareModified,
}) => {
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { setPreviewFile } = useContext(PreviewContext);
  const { openDirectory } = useContext(DocumentsContext);
  const { copyTextToClipboard } = useCopy();

  const [isLoading, setIsLoading] = useState(false);
  const [secureShare, setSecureShare] =
    useState<SecureDocumentShareResponse | null>(null);
  const [expirationDays, setExpirationDays] = useState<number>(2);
  const [emailAddresses, setEmailAddresses] = useState<string>('');
  const [emailSubject, setEmailSubject] = useState<string>('');
  const [emailMessage, setEmailMessage] = useState<string>('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isExpiringShare, setIsExpiringShare] = useState(false);
  const [showExpireConfirm, setShowExpireConfirm] = useState(false);

  // Set default subject when email form is shown
  const handleShowEmailForm = useCallback(() => {
    if (!emailSubject) {
      const fileName =
        getDocumentName(document?.name || document?.link || '') || 'document';
      setEmailSubject(`Document shared with you: ${fileName}`);
    }
    setShowEmailForm(true);
  }, [emailSubject, document]);

  const shareUrl = secureShare
    ? `${window.location.origin}/secure-share/${secureShare.token}`
    : '';

  // Generate user-friendly path from the current directory and filename
  const getUserFriendlyPath = useCallback(() => {
    if (!document) return '';

    // Extract filename from the full document link
    // const filename = document.name || document.link.split('/').pop() || '';
    const filename = getDocumentName(document.name || document.link) || '';

    // Combine with current open directory to show user-visible path
    if (openDirectory) {
      return `${openDirectory}/${filename}`;
    } else {
      // If in root directory, just show the filename
      return filename;
    }
  }, [document, openDirectory]);

  const checkExisting = useCallback(async () => {
    if (!document || !token) return;

    setIsLoading(true);
    try {
      const response = await checkExistingSecureShare({
        documentLink: document.link,
        companyId,
        token,
      });

      if (response.exists) {
        setSecureShare({
          id: response.id!,
          token: response.token!,
          expires_at: response.expires_at!,
          existing: true,
        });
      }
    } catch (error) {
      addToast(
        'error',
        'Unable to load secure document share information. Please try again.',
        { error },
      );
    } finally {
      setIsLoading(false);
    }
  }, [document, token, companyId, addToast]);

  // Check for existing share when dialog opens
  useEffect(() => {
    if (isOpen && document && token) {
      checkExisting();
    }
  }, [isOpen, document, token, checkExisting]);

  const handleGenerateLink = useCallback(async () => {
    if (!document || !token) return;

    setIsLoading(true);
    try {
      const response = await createSecureDocumentShare({
        documentLink: document.link,
        companyId,
        expirationHours: expirationDays * 24,
        token,
      });

      setSecureShare(response);
      addToast('success', 'Secure share link generated successfully');
    } catch (error) {
      addToast('error', 'Failed to generate secure share link', { error });
    } finally {
      onSecureShareModified?.(); // Notify parent component that a secure share was created
      setIsLoading(false);
    }
  }, [
    document,
    token,
    companyId,
    expirationDays,
    addToast,
    onSecureShareModified,
  ]);

  const handleCopyLink = useCallback(async () => {
    if (!shareUrl) return;

    try {
      await copyTextToClipboard(shareUrl);
      addToast('success', 'Link copied to clipboard');
    } catch (error) {
      addToast('error', 'Failed to copy link');
    }
  }, [shareUrl, copyTextToClipboard, addToast]);

  const handlePreviewDocument = useCallback(async () => {
    if (!document || !token) return;

    try {
      const response = await downloadDocument({
        companyId,
        link: document.link,
        token,
      });

      if (response.document?.data?.link) {
        setPreviewFile(response.document.data.link);
      }
    } catch (error) {
      addToast('error', 'Failed to preview document', { error });
    }
  }, [document, token, companyId, setPreviewFile, addToast]);

  const handleSendEmail = useCallback(async () => {
    if (!secureShare || !emailAddresses.trim()) return;

    const emailList = emailAddresses
      .split(/[,\n]/)
      .map((email) => email.trim())
      .filter((email) => email);

    if (emailList.length === 0) {
      addToast('error', 'Please enter at least one email address');
      return;
    }

    setIsSendingEmail(true);
    try {
      const response = await sendSecureShareEmail({
        secureShareId: secureShare.id,
        companyId,
        emailAddresses: emailList,
        subject: emailSubject || undefined,
        message: emailMessage || undefined,
        token,
      });

      addToast(
        'success',
        `Email sent successfully to ${response.recipients.length} recipient(s)`,
      );
      setShowEmailForm(false);
      setEmailAddresses('');
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      addToast('error', 'Failed to send email', { error });
    } finally {
      setIsSendingEmail(false);
    }
  }, [
    secureShare,
    emailAddresses,
    addToast,
    companyId,
    emailSubject,
    emailMessage,
    token,
  ]);

  const handleExpireShare = useCallback(async () => {
    if (!secureShare || !token) return;

    setIsExpiringShare(true);
    try {
      await expireSecureShare({
        secureShareId: secureShare.id,
        companyId,
        token,
      });

      // Update the local state to reflect the expired status
      setSecureShare({
        ...secureShare,
        expires_at: new Date().toISOString(),
      });

      addToast('success', 'Secure share link has been expired');
    } catch (error) {
      addToast('error', 'Failed to expire secure share link', { error });
    } finally {
      setIsExpiringShare(false);
      onSecureShareModified?.(); // Notify parent component that a secure share was expired
    }
  }, [secureShare, token, companyId, addToast, onSecureShareModified]);

  const handleShowExpireConfirm = useCallback(() => {
    setShowExpireConfirm(true);
  }, []);

  const handleCloseExpireConfirm = useCallback(() => {
    setShowExpireConfirm(false);
  }, []);

  const handleConfirmExpire = useCallback(async () => {
    setShowExpireConfirm(false);
    await handleExpireShare();
  }, [handleExpireShare]);

  const handleClose = useCallback(() => {
    setSecureShare(null);
    setExpirationDays(2);
    setEmailAddresses('');
    setEmailSubject('');
    setEmailMessage('');
    setShowEmailForm(false);
    setIsExpiringShare(false);
    setShowExpireConfirm(false);
    onClose();
  }, [onClose]);

  const expiresAt = secureShare ? new Date(secureShare.expires_at) : null;
  const isExpired = expiresAt ? expiresAt < new Date() : false;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={
        secureShare ? 'View Secure Share Link' : 'Generate Secure Share Link'
      }
      isWide={true}
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={8}>
          {isLoading && (
            <Flex isYCentered={true} isXCentered={true} padding={8}>
              <Loading size={32} />
            </Flex>
          )}

          {!isLoading && !secureShare && (
            <>
              <Alert type="info" icon="info">
                <Text type="small">
                  Click &ldquo;Generate Secure Link&rdquo; below to generate a
                  secure, temporary link to share with anyone—no login required.
                  <br />
                  <br />
                  • Only those with the link can view the document.
                  <br />
                  • Once generated, the link expires after the duration set
                  below. There is no limit to the number of times a link can be
                  opened before it has expired.
                  <br />
                  • While the link is still live (not expired), you can view it
                  any time by coming back here.
                  <br />
                  <br />
                  <strong>Note:</strong> Share this link only with intended
                  recipients. Anyone with access to the link can view the
                  document.
                </Text>
              </Alert>

              <Flex gap={4} isYCentered={true}>
                <Text type="small" fontWeight="semi-bold">
                  Path:
                </Text>
                <Text type="small" color="secondary">
                  {getUserFriendlyPath()}
                </Text>
              </Flex>

              <Flex gap={4} isYCentered={true}>
                <Text type="small" fontWeight="semi-bold">
                  Link expires after:
                </Text>
                <Column width={80}>
                  <Input
                    label=""
                    isLabelHidden={true}
                    type="number"
                    value={expirationDays.toString()}
                    onChange={(e) =>
                      setExpirationDays(
                        Math.max(
                          2,
                          Math.min(30, parseInt(e.target.value) || 2),
                        ),
                      )
                    }
                    min={2}
                    max={30}
                  />
                </Column>
                <Text type="small">days</Text>
              </Flex>

              <ButtonGroup align="space-between">
                <Button
                  value="Preview Document"
                  intent="secondary"
                  iconLeft="eye"
                  onClick={handlePreviewDocument}
                />
                <Button
                  value="Generate Secure Link"
                  intent="primary"
                  onClick={handleGenerateLink}
                  isDisabled={isLoading}
                />
              </ButtonGroup>
            </>
          )}

          {!isLoading && secureShare && (
            <>
              {isExpired ? (
                <Alert type="danger" icon="warning">
                  This secure share link has expired.
                </Alert>
              ) : (
                <Alert type="success" icon="check">
                  Secure share link is active and will expire on{' '}
                  {expiresAt?.toLocaleDateString()} at{' '}
                  {expiresAt?.toLocaleTimeString()}.
                </Alert>
              )}

              <Flex gap={4} isYCentered={true}>
                <Text type="small" fontWeight="semi-bold">
                  Path:
                </Text>
                <Text type="small" color="secondary">
                  {getUserFriendlyPath()}
                </Text>
              </Flex>

              {!isExpired && (
                <>
                  <Flex isColumn={true} gap={4}>
                    <Text type="small" fontWeight="semi-bold">
                      Share Link:
                    </Text>
                    <Flex gap={4} isYCentered>
                      <Input
                        label=""
                        isLabelHidden
                        value={shareUrl}
                        onChange={() => {}} // Read-only, no changes allowed
                        isDisabled
                      />
                      <Button
                        value="Copy"
                        intent="secondary"
                        iconLeft="link"
                        size="small"
                        onClick={handleCopyLink}
                      />
                    </Flex>
                  </Flex>

                  {!showEmailForm ? (
                    <ButtonGroup align="space-between">
                      <Flex gap={4}>
                        <Button
                          value="Preview Document"
                          intent="secondary"
                          iconLeft="eye"
                          onClick={handlePreviewDocument}
                        />
                        <Button
                          value="Expire Link"
                          intent="danger-secondary"
                          iconLeft="clock"
                          onClick={handleShowExpireConfirm}
                          isLoading={isExpiringShare}
                          isDisabled={isExpiringShare}
                        />
                      </Flex>
                      <Button
                        value="Send via Email"
                        intent="primary"
                        iconLeft="send"
                        onClick={handleShowEmailForm}
                      />
                    </ButtonGroup>
                  ) : (
                    <Flex isColumn={true} gap={6}>
                      <Text type="small" fontWeight="semi-bold">
                        Send via Email:
                      </Text>

                      <Textarea
                        label="Recipient(s)"
                        placeholder="Multiple email addresses can be separated by commas or listed on separate lines"
                        value={emailAddresses}
                        onChange={(e) => setEmailAddresses(e.target.value)}
                        isRequired={true}
                        rows={3}
                      />

                      <Input
                        label="Subject (optional)"
                        placeholder="Custom email subject"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                      />

                      <Textarea
                        label="Message (optional)"
                        placeholder="Custom email message"
                        value={emailMessage}
                        onChange={(e) => setEmailMessage(e.target.value)}
                        rows={4}
                      />

                      <ButtonGroup align="right">
                        <Button
                          value="Cancel"
                          intent="secondary"
                          onClick={() => setShowEmailForm(false)}
                          isDisabled={isSendingEmail}
                        />
                        <Button
                          value="Send Email"
                          intent="primary"
                          iconLeft="send"
                          onClick={handleSendEmail}
                          isDisabled={!emailAddresses.trim() || isSendingEmail}
                          isLoading={isSendingEmail}
                        />
                      </ButtonGroup>
                    </Flex>
                  )}
                </>
              )}
            </>
          )}
        </Flex>
      </Dialog.Content>
      <ConfirmDialog
        isOpen={showExpireConfirm}
        isLoading={isExpiringShare}
        title="Confirm Expiration"
        message="Are you sure you want to immediately expire this link? This action cannot be undone."
        onClose={handleCloseExpireConfirm}
        onConfirm={handleConfirmExpire}
      />
    </Dialog>
  );
};
